/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { Filter } from '../../../model/Filter';
import { Statistics } from '../../../model/Statistics';
import { BaseController } from '../../BaseController';

class StatisticsBoardController extends BaseController {
    /** @ngInject */
    constructor(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log, $translate,
        statisticsService, domainService, siteService, equipmentService, filterService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.statisticsService = statisticsService;
        this.domainService = domainService;
        this.siteService = siteService;
        this.equipmentService = equipmentService;
        this.filterService = filterService;
        this.$translate = $translate;
        this.$log.debug('Controller \'StatisticsBoard\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.statistics = [];
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.graphFilter = Filter.equalFilterEmpty('graph', 'ui.statistics.graphtype', 'SELECT', [{
            id: 'ACTION_REQUESTS_BY_CAUSE',
            labelKey: 'ui.statistics.graph.ACTION_REQUESTS_BY_CAUSE'
        }, {
            id: 'ACTION_REQUESTS_BY_STATUS',
            labelKey: 'ui.statistics.graph.ACTION_REQUESTS_BY_STATUS'
        }, {
            id: 'PREVENTIVE_SHEETS_BY_STATUS',
            labelKey: 'ui.statistics.graph.PREVENTIVE_SHEETS_BY_STATUS'
        }], graphId => this.selectGraph(graphId), true);

        this.equipmentFilter = Filter.equalFilterEmpty('equipment.id', 'model.equipment.equipment', 'SELECT', []);

        this.siteFilter = Filter.equalFilterEmpty('equipment.site.id', 'model.site.site', 'SELECT_AUTOCOMPLETE', [],
            siteId => this.getFilterEquipments(siteId));

        this.domainFilter = Filter.equalFilterEmpty('equipment.site.domain.id', 'model.domain.domain', 'SELECT',
            [], domainId => this.getFilterSites(domainId));

        const startDate = new Date();
        startDate.setUTCMonth(0, 1);
        startDate.setHours(0, 0, 0, 0);
        this.startDateFilter = Filter.greaterOrEqualDateTimeFilter('creationDate', startDate,
            'ui.statistics.from', undefined, false, true);

        this.filters = [
            this.graphFilter,
            this.domainFilter,
            this.siteFilter,
            this.equipmentFilter,
            this.startDateFilter,
            Filter.lowerDateTimeFilter('creationDate', new Date(), 'ui.statistics.before')
        ];
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        // No command in the view.
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains()
            .then(() => this.getFilterSites(this.domainFilter.value))
            .then(() => this.getFilterEquipments(this.siteFilter.value));
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of sites for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of sites.
     */
    getFilterSites(domainId) {
        return this.filterService.getOrClearFilterOptions(this.siteService, this.siteFilter, 'name',
            [Filter.equalFilter('domain.id', domainId)], domainId)
            .then(() => this.getFilterEquipments(this.siteFilter.value));
    }

    /**
     * Gets the list of equipments for a given site.
     *
     * @param {Number} [siteId] Site ID, may be undefined if no site is selected.
     * @return {Object[]} Promise providing an array of equipments.
     */
    getFilterEquipments(siteId) {
        return this.filterService.getOrClearFilterOptions(this.equipmentService, this.equipmentFilter, 'name',
            [Filter.equalFilter('site.id', siteId)], siteId);
    }

    /**
     * Select a type of graph, and initializes the start date.
     *
     * @param {String} graphId ID of the graph selected.
     */
    selectGraph(graphId) {
        this.statistics.splice(0);
    }

    /**
     * Builds chart data to show the number of action requests group by cause.
     *
     * @param {Object} statistics Statistics received from the server.
     */
    buildActionRequestsByCauseGraph(statistics) {
        for (const causeName in statistics.data) {
            if (statistics.data.hasOwnProperty(causeName)) {
                this.statistics.push({
                    label: causeName,
                    value: statistics.data[causeName]
                });
            }
        }
    }

    /**
     * Builds chart data to show the number of action requests group by status.
     *
     * @param {Object} statistics Statistics received from the server.
     */
    buildActionRequestsByStatusGraph(statistics) {
        for (const status in statistics.data) {
            if (statistics.data.hasOwnProperty(status)) {
                const statusSerie = {};
                this.$translate(`model.actionrequest.status.${status}`).then(
                    translation => statusSerie.key = translation
                );
                statusSerie.values = statistics.data[status];
                this.statistics.push(statusSerie);
            }
        }
    }

    /**
     * Builds chart data to show the number of preventive sheets group by status.
     *
     * @param {Object} statistics Statistics received from the server.
     */
    buildPreventiveSheetsByStatusGraph(statistics) {
        let openedPreventiveSheetsSerie;
        let completedPreventiveSheetsSerie;
        for (const status in statistics.data) {
            if (statistics.data.hasOwnProperty(status)) {
                const statusSerie = {};
                this.$translate(`model.preventivesheet.status.${status}`).then(
                    translation => statusSerie.key = translation
                );
                statusSerie.values = statistics.data[status];
                this.statistics.push(statusSerie);

                if (this.graphFilter.value === 'PREVENTIVE_SHEETS_BY_STATUS') {
                    if (status === 'OPENED') {
                        openedPreventiveSheetsSerie = statusSerie;
                    } else if (status === 'COMPLETED') {
                        completedPreventiveSheetsSerie = statusSerie;
                    }
                }
            }
        }

        this.generateTotalOpenedSerie(statistics.initNumberOfOpenedPreventiveSheets, openedPreventiveSheetsSerie,
            completedPreventiveSheetsSerie);
    }

    /**
     * Generates an additional serie providing the evolution of the total number of opened preventive sheets.
     */
    generateTotalOpenedSerie(initOpened, openedPreventiveSheetsSerie, completedPreventiveSheetsSerie) {
        if (openedPreventiveSheetsSerie && completedPreventiveSheetsSerie) {
            const totalOpenedSerie = {
                values: [],
                classed: 'gmao-preventive-sheets-total-opened'
            };
            this.$translate('ui.statistics.totalopenedpreventivesheets').then(
                translation => totalOpenedSerie.key = translation
            );

            let totalOpened = initOpened;
            for (let i = 0; i < openedPreventiveSheetsSerie.values.length; i++) {
                totalOpened += openedPreventiveSheetsSerie.values[i].y - completedPreventiveSheetsSerie.values[i].y;
                totalOpenedSerie.values.push({
                    x: openedPreventiveSheetsSerie.values[i].x,
                    y: totalOpened
                });
            }
            this.statistics.push(totalOpenedSerie);
        }
    }

    /**
     * Gets the statistics.
     *
     * @return {Promise} A promise providing the statistics, or a rejection if no type of graph was selected.
     */
    reload() {
        let promise;
        if (this.graphFilter.value) {
            // Removes the 'graph' filter from the filters that are sent in the request.
            const requestFilters = this.filterService.buildFilters(this.filters);
            const graphFilterIndex = requestFilters.findIndex(filter => filter.key === 'graph');
            if (graphFilterIndex >= 0) {
                requestFilters.splice(graphFilterIndex, 1);
            }

            promise = this.statisticsService.getChartData(this.graphFilter.value, requestFilters).then(statistics => {
                this.statistics.splice(0);
                switch (this.graphFilter.value) {
                case 'ACTION_REQUESTS_BY_CAUSE':
                    this.buildActionRequestsByCauseGraph(statistics);
                    break;
                case 'ACTION_REQUESTS_BY_STATUS':
                    this.buildActionRequestsByStatusGraph(statistics);
                    break;
                case 'PREVENTIVE_SHEETS_BY_STATUS':
                    this.buildPreventiveSheetsByStatusGraph(statistics);
                    break;
                default:
                    break;
                }
            });
        } else {
            promise = false;
        }
        return promise;
    }
}

export const statisticsBoardComponent = {
    template: require('./statistics-board.html'),
    controller: StatisticsBoardController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/statistics/component/StatisticsBoard.js